import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  IconButton,
  Divider,
  Alert,
  Avatar,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import SyncIcon from "@mui/icons-material/Sync";
import SettingsIcon from "@mui/icons-material/Settings";

const IntegrationConfigModal = ({ open, onClose, data, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    logo: "",
    category: "",
    description: "",
    apiKey: "",
    apiSecret: "",
    webhookUrl: "",
    syncEnabled: true,
    syncInterval: 15,
    fields: {
      customField1: "",
      customField2: "",
      customField3: "",
    },
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        logo: data.logo || "",
        category: data.category || "",
        description: data.description || "",
        apiKey: data.apiKey || "",
        apiSecret: data.apiSecret || "",
        webhookUrl: data.webhookUrl || "",
        syncEnabled: data.syncEnabled !== false,
        syncInterval: data.syncInterval || 15,
        fields: data.fields || {
          customField1: "",
          customField2: "",
          customField3: "",
        },
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFieldChange = (field, value) => {
    setFormData({
      ...formData,
      fields: {
        ...formData.fields,
        [field]: value,
      },
    });
  };

  const handleTestConnection = () => {
    alert("Testing connection to " + formData.name + "...");
    setTimeout(() => {
      alert("Connection successful!");
    }, 1000);
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      logo: "",
      category: "",
      description: "",
      apiKey: "",
      apiSecret: "",
      webhookUrl: "",
      syncEnabled: true,
      syncInterval: 15,
      fields: {
        customField1: "",
        customField2: "",
        customField3: "",
      },
    });
    onClose();
  };

  // Get integration-specific fields based on name
  const getIntegrationFields = () => {
    switch (formData.name) {
      case "Salesforce":
        return {
          customField1: { label: "Instance URL", placeholder: "https://yourinstance.salesforce.com" },
          customField2: { label: "Username", placeholder: "user@company.com" },
          customField3: { label: "Security Token", placeholder: "Enter security token" },
        };
      case "HubSpot":
        return {
          customField1: { label: "Portal ID", placeholder: "12345678" },
          customField2: { label: "Access Token", placeholder: "Enter access token" },
          customField3: { label: "Pipeline ID", placeholder: "default" },
        };
      case "Google Calendar":
        return {
          customField1: { label: "Calendar ID", placeholder: "primary" },
          customField2: { label: "OAuth Client ID", placeholder: "Enter client ID" },
          customField3: { label: "OAuth Client Secret", placeholder: "Enter client secret" },
        };
      case "Stripe":
        return {
          customField1: { label: "Publishable Key", placeholder: "pk_live_..." },
          customField2: { label: "Secret Key", placeholder: "sk_live_..." },
          customField3: { label: "Webhook Secret", placeholder: "whsec_..." },
        };
      case "Zoom":
        return {
          customField1: { label: "Account ID", placeholder: "Enter account ID" },
          customField2: { label: "Client ID", placeholder: "Enter client ID" },
          customField3: { label: "Client Secret", placeholder: "Enter client secret" },
        };
      default:
        return {
          customField1: { label: "API Endpoint", placeholder: "https://api.example.com" },
          customField2: { label: "Username", placeholder: "username" },
          customField3: { label: "Additional Config", placeholder: "" },
        };
    }
  };

  const integrationFields = getIntegrationFields();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {formData.logo && (
              <Avatar src={formData.logo} alt={formData.name} sx={{ width: 40, height: 40 }} />
            )}
            <Typography variant="h5" fontWeight={600}>
              Configure {formData.name || "Integration"}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Alert severity="info">
            {formData.description || "Configure the integration settings to connect with " + formData.name}
          </Alert>

          {/* Authentication */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Authentication
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="API Key"
                value={formData.apiKey}
                onChange={(e) => handleChange("apiKey", e.target.value)}
                type="password"
                placeholder="Enter your API key"
              />
              <TextField
                fullWidth
                label="API Secret"
                value={formData.apiSecret}
                onChange={(e) => handleChange("apiSecret", e.target.value)}
                type="password"
                placeholder="Enter your API secret"
              />
            </Box>
          </Box>

          <Divider />

          {/* Integration-Specific Fields */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Integration Settings
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {integrationFields.customField1.label && (
                  <TextField
                    fullWidth
                    label={integrationFields.customField1.label}
                    value={formData.fields.customField1}
                    onChange={(e) => handleFieldChange("customField1", e.target.value)}
                    placeholder={integrationFields.customField1.placeholder}
                  />
                )}
                {integrationFields.customField2.label && (
                  <TextField
                    fullWidth
                    label={integrationFields.customField2.label}
                    value={formData.fields.customField2}
                    onChange={(e) => handleFieldChange("customField2", e.target.value)}
                    placeholder={integrationFields.customField2.placeholder}
                  />
                )}
                {integrationFields.customField3.label && (
                  <TextField
                    fullWidth
                    label={integrationFields.customField3.label}
                    value={formData.fields.customField3}
                    onChange={(e) => handleFieldChange("customField3", e.target.value)}
                    placeholder={integrationFields.customField3.placeholder}
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Webhook Configuration */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" fontWeight={600}>
                Webhook Configuration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={formData.webhookUrl}
                  onChange={(e) => handleChange("webhookUrl", e.target.value)}
                  placeholder="https://your-webhook-url.com/webhook"
                  helperText="URL to receive webhook notifications from this integration"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Sync Settings */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Sync Settings
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.syncEnabled}
                    onChange={(e) => handleChange("syncEnabled", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Enable Auto-Sync
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically sync data at regular intervals
                    </Typography>
                  </Box>
                }
              />
              {formData.syncEnabled && (
                <TextField
                  fullWidth
                  label="Sync Interval (minutes)"
                  type="number"
                  value={formData.syncInterval}
                  onChange={(e) => handleChange("syncInterval", parseInt(e.target.value))}
                  InputProps={{ inputProps: { min: 5, max: 1440 } }}
                  helperText="How often to sync data (5-1440 minutes)"
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          icon={<SyncIcon />}
          iconPosition="left"
          text="Test Connection"
          variant="outlined"
          onClick={handleTestConnection}
        />
        <Box sx={{ flex: 1 }} />
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text="Save Configuration"
          onClick={handleSubmit}
          disabled={!formData.apiKey.trim()}
        />
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationConfigModal;
